// Panel dock layout — pure data: types, defaults and validation for the
// per-player HUD layout store (./usePanelLayout.ts). No React, no zustand,
// so the store tests can exercise validation without a DOM.

/** The six dockable HUD panels, keyed by their integration point. */
export type PanelId = 'doctrine' | 'info' | 'eventLog' | 'victory' | 'minimap' | 'debug';

/** A dock edge panels can live on (v1: no floating/free placement). */
export type PanelDock = 'left' | 'right' | 'bottom';

export interface PanelLayout {
  dock: PanelDock;
  collapsed: boolean;
  /**
   * CSS px size; null = the panel's natural/default size. Width for the side
   * docks, height for the bottom dock. Clamped by `clampPanelSize`.
   */
  size: number | null;
  /** Stack position within the dock (lower = closer to the screen edge). */
  order: number;
}

export type PanelLayoutMap = Record<PanelId, PanelLayout>;

export const PANEL_IDS = ['doctrine', 'info', 'eventLog', 'victory', 'minimap', 'debug'] as const;

/**
 * Defaults ARE today's interface: a player who never touches a dock must see
 * exactly the pre-dock layout. Two defaults read `collapsed: true` — `debug`
 * (per the panel-docks brief; it only mounts in dev anyway) and `eventLog`
 * (which has always booted as just its header row) — so production screens
 * are untouched.
 */
export const DEFAULT_PANEL_LAYOUT: PanelLayoutMap = {
  doctrine: { dock: 'left', collapsed: false, size: null, order: 0 },
  info: { dock: 'right', collapsed: false, size: null, order: 0 },
  eventLog: { dock: 'bottom', collapsed: true, size: null, order: 0 },
  victory: { dock: 'right', collapsed: false, size: null, order: 1 },
  minimap: { dock: 'right', collapsed: false, size: null, order: 2 },
  debug: { dock: 'left', collapsed: true, size: null, order: 1 },
};

/** Side docks resize by width (px). */
export const SIDE_DOCK_MIN = 220;
export const SIDE_DOCK_MAX = 560;
/** Bottom dock resizes by height (px). */
export const BOTTOM_DOCK_MIN = 96;
export const BOTTOM_DOCK_MAX = 360;

export function isPanelDock(v: unknown): v is PanelDock {
  return v === 'left' || v === 'right' || v === 'bottom';
}

export function isPanelId(v: unknown): v is PanelId {
  return (PANEL_IDS as readonly unknown[]).includes(v);
}

export function clampPanelSize(dock: PanelDock, size: number): number {
  const min = dock === 'bottom' ? BOTTOM_DOCK_MIN : SIDE_DOCK_MIN;
  const max = dock === 'bottom' ? BOTTOM_DOCK_MAX : SIDE_DOCK_MAX;
  return Math.min(max, Math.max(min, size));
}

/** The uid a signed-out player's layout is stored under. */
export const GUEST_UID = 'guest';

/** One key per player: each signed-in account (and guest) owns its layout. */
export function panelStorageKey(uid: string): string {
  return `aod.layout.v1:${uid}`;
}

/**
 * Validate a parsed localStorage payload. Unknown shapes are rejected as a
 * whole (the caller falls back to defaults) rather than half-applied; sizes
 * outside the clamp are pulled back in instead of rejected, so a grown panel
 * survives a later clamp change.
 */
export function parseStoredLayout(raw: unknown): PanelLayoutMap | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const rec = raw as Record<string, unknown>;
  const out = {} as PanelLayoutMap;
  let sawAny = false;
  for (const id of PANEL_IDS) {
    const def = DEFAULT_PANEL_LAYOUT[id];
    const entry = rec[id];
    if (entry === undefined) {
      out[id] = { ...def };
      continue;
    }
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const e = entry as Record<string, unknown>;
    if (!isPanelDock(e.dock)) return null;
    if (typeof e.collapsed !== 'boolean') return null;
    if (e.size !== null && (typeof e.size !== 'number' || !Number.isFinite(e.size))) return null;
    const order = typeof e.order === 'number' && Number.isFinite(e.order) ? e.order : def.order;
    out[id] = {
      dock: e.dock,
      collapsed: e.collapsed,
      size: e.size === null ? null : clampPanelSize(e.dock, e.size),
      order,
    };
    sawAny = true;
  }
  // `{}` (or a payload with no panel entries at all) is "no stored layout",
  // not "a stored layout of all defaults" — the caller distinguishes the two.
  return sawAny ? out : null;
}
